import {
  dictionaryOf,
  type TokenJudgement,
  toTokenJudgements,
  WORD_INTENT_PATH,
  type WordIntentError,
  type WordIntentOutcome,
  type WordIntentRequest,
  wordIntentErrorSchema,
  wordIntentResponseSchema,
} from "../contracts/word-intent";

export type WordIntentResult =
  | {
      readonly kind: WordIntentOutcome;
      /** One per judged token, ready for a `judge` edit. */
      readonly judgements: readonly TokenJudgement[];
      /** Token id → dictionary word, for every word sent, in every outcome. */
      readonly dictionary: ReadonlyMap<string, boolean>;
    }
  | { readonly kind: WordIntentError["error"] }
  // The Worker could not be reached, or answered something off-contract.
  | { readonly kind: "failed" };

export interface RequestWordIntentInput {
  readonly request: WordIntentRequest;
  /** The host's token source, e.g. `fetchToken` from `@bindery/auth-consumer/browser`. */
  readonly getToken: () => Promise<string | null>;
  /** The instant the judgements are stamped with. */
  readonly now: () => number;
  readonly fetch?: typeof fetch;
}

/**
 * Ask the Worker which word the child most likely said at each judged
 * token. Never throws: every way it can fail is a result, and the page
 * carries on with the words unjudged.
 */
export const requestWordIntent = async ({
  request,
  getToken,
  now,
  fetch: doFetch = (input, init) => fetch(input, init),
}: RequestWordIntentInput): Promise<WordIntentResult> => {
  const token = await getToken();
  if (token === null) return { kind: "unauthenticated" };
  let body: unknown;
  try {
    const response = await doFetch(WORD_INTENT_PATH, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify(request),
    });
    body = await response.json();
  } catch {
    return { kind: "failed" };
  }
  const answered = wordIntentResponseSchema.safeParse(body);
  if (answered.success) {
    return {
      kind: answered.data.outcome,
      judgements: toTokenJudgements(answered.data, now()),
      dictionary: dictionaryOf(answered.data),
    };
  }
  const refused = wordIntentErrorSchema.safeParse(body);
  return refused.success ? { kind: refused.data.error } : { kind: "failed" };
};
